import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import EmptyState from "../components/common/EmptyState";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { asArray, downloadBlob, formatDate, unwrapData } from "../lib/catalog";
import { isDownloadEnabled, releaseLabel } from "../lib/documentRelease";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWriteBookings, roleFromProfile } from "../lib/agencyAccess";

function openBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    downloadBlob(blob, fileName);
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

export default function DocumentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [canCreateBooking, setCanCreateBooking] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [payload, profile] = await Promise.all([
          agentApi.get("/travel-partner/reports/documents", {
            params: { page: 1, limit: 100 },
          }),
          fetchCurrentAgent().catch(() => null),
        ]);
        if (!alive) return;
        setCanCreateBooking(canWriteBookings(roleFromProfile(profile)));
        const data = unwrapData(payload);
        setRows(asArray(data));
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_DOCUMENTS");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  async function downloadDocument(documentId, fileName) {
    try {
      const blob = await agentApi.get(`/travel-partner/documents/${documentId}/download`, { responseType: "blob" });
      downloadBlob(blob, fileName || `document-${documentId}`);
      showSuccessAction("DOWNLOAD_DOCUMENT");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_DOCUMENT");
    }
  }

  async function viewDocument(documentId, fileName) {
    try {
      const blob = await agentApi.get(`/travel-partner/documents/${documentId}/download`, { responseType: "blob" });
      openBlob(blob, fileName || `document-${documentId}`);
      showSuccessAction("OPEN_DOCUMENT");
    } catch (viewError) {
      showApiError(viewError, "OPEN_DOCUMENT");
    }
  }

  const filtered = rows.filter((row) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [row.booking_reference, row.passenger_name, row.document_type, row.review_status, row.file_name, row.release_state]
      .some((value) => String(value || "").toLowerCase().includes(query));
  });

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((row) => {
      const key = row.booking_id || row.booking_reference || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          booking_id: row.booking_id,
          booking_reference: row.booking_reference || "Booking",
          items: [],
        });
      }
      map.get(key).items.push(row);
    });
    return Array.from(map.values());
  }, [filtered]);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Documents"
          title="Pilgrim documents across your bookings"
          description="Passport, photo, and supporting files stay available for processing. Issued visas unlock after the booking payment is complete."
          items={[DASHBOARD_CRUMB, { label: "Documents" }]}
          actions={
            <div className="page-toolbar">
              <input
                type="search"
                className="form-control page-search"
                placeholder="Search documents"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {canCreateBooking ? (
              <Link to="/packages" className="btn btn-primary">
                <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                Add booking
              </Link>
              ) : null}
            </div>
          }
        />

        {loading ? <PageLoader message="Loading documents..." /> : null}

        {!loading && grouped.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <EmptyState
                variant="documents"
                title={search.trim() && rows.length > 0
                  ? "No documents match your search"
                  : "No documents found."}
                description={search.trim() && rows.length > 0
                  ? "Try a different search term, or open a booking to upload files."
                  : "Upload passport, photo, and supporting files from a booking."}
                action={(
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <Link to="/bookings" className="btn btn-outline-primary">
                      View bookings
                    </Link>
                    {canCreateBooking ? (
                    <Link to="/packages" className="btn btn-primary">
                      <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                      Add booking
                    </Link>
                    ) : null}
                  </div>
                )}
              />
            </div>
          </div>
        ) : null}

        {grouped.map((group) => (
          <div className="card border-0 shadow-sm mb-4" key={group.booking_id || group.booking_reference}>
            <div className="card-body p-24">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div className="min-w-0">
                  <h5 className="mb-1">
                    {group.booking_id ? (
                      <Link to={`/bookings/${group.booking_id}`} className="agent-table-link">
                        {group.booking_reference}
                      </Link>
                    ) : (
                      group.booking_reference
                    )}
                  </h5>
                  <div className="small text-secondary">{group.items.length} document{group.items.length === 1 ? "" : "s"}</div>
                </div>
                {group.booking_id ? (
                  <Link
                    to={`/bookings/${group.booking_id}`}
                    className="agent-icon-btn"
                    title="View booking documents"
                    aria-label="View booking documents"
                  >
                    <iconify-icon icon="solar:eye-outline"></iconify-icon>
                  </Link>
                ) : null}
              </div>
              <Table>
                <thead>
                  <tr>
                    <th>Pilgrim</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Release</th>
                    <th>Uploaded</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((row) => (
                    <tr key={row.document_id}>
                      <td><PersonCell name={row.passenger_name} /></td>
                      <td>{row.document_type || "-"}</td>
                      <td><StatusBadge value={row.review_status || "-"} /></td>
                      <td>
                        <StatusBadge value={row.release_state}>{releaseLabel(row.release_state)}</StatusBadge>
                      </td>
                      <td>{formatDate(row.created_date)}</td>
                      <td className="text-end">
                        <div className="d-inline-flex align-items-center justify-content-end gap-2">
                          <button
                            type="button"
                            className="agent-icon-btn"
                            title={isDownloadEnabled(row) ? "View document" : "Locked"}
                            aria-label={isDownloadEnabled(row) ? "View document" : "Locked"}
                            disabled={!isDownloadEnabled(row)}
                            onClick={() => viewDocument(row.document_id, row.file_name)}
                          >
                            <iconify-icon icon="solar:eye-outline"></iconify-icon>
                          </button>
                          <button
                            type="button"
                            className="agent-icon-btn"
                            title={isDownloadEnabled(row) ? "Download" : "Locked"}
                            aria-label={isDownloadEnabled(row) ? "Download" : "Locked"}
                            disabled={!isDownloadEnabled(row)}
                            onClick={() => downloadDocument(row.document_id, row.file_name)}
                          >
                            <iconify-icon icon="solar:download-minimalistic-outline"></iconify-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        ))}
      </div>
    </AgentLayout>
  );
}
